import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { executionApi } from '@services/executionApi'
import type { InitiativeData, AlertData, ExecutionHealthEntry } from '@services/executionApi'

interface ExecutionState {
  initiatives: InitiativeData[]
  alerts: AlertData[]
  health: ExecutionHealthEntry[]
  dashboard: {
    total: number
    status_counts: Record<string, number>
    completion_rate: number
    blocked_items: Array<{ id: number; title: string; blockers: any[]; days_blocked: number }>
  } | null
  escalatedBlockers: Array<{ id: number; title: string; blockers: any[]; days_blocked: number }>
  replanTrigger: any | null
  stalled: Array<{ id: number; title: string; days_stalled: number }>
  loading: boolean
  error: string | null
}

const initialState: ExecutionState = {
  initiatives: [],
  alerts: [],
  health: [],
  dashboard: null,
  escalatedBlockers: [],
  replanTrigger: null,
  stalled: [],
  loading: false,
  error: null,
}

export const fetchExecutionData = createAsyncThunk(
  'execution/fetchAll',
  async (companyId: number) => {
    const [initRes, alertRes, dashRes, healthRes] = await Promise.all([
      executionApi.listInitiatives(companyId),
      executionApi.listAlerts(companyId),
      executionApi.getDashboard(companyId),
      executionApi.getHealth(companyId),
    ])
    return {
      initiatives: initRes.initiatives || [],
      alerts: alertRes.alerts || [],
      dashboard: dashRes,
      health: healthRes.health || [],
      escalatedBlockers: healthRes.escalated_blockers || [],
      replanTrigger: healthRes.replan_trigger || null,
      stalled: healthRes.stalled || [],
    }
  }
)

const executionSlice = createSlice({
  name: 'execution',
  initialState,
  reducers: {
    clearExecution: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExecutionData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchExecutionData.fulfilled, (state, action) => {
        state.loading = false
        state.initiatives = action.payload.initiatives
        state.alerts = action.payload.alerts
        state.dashboard = action.payload.dashboard
        state.health = action.payload.health
        state.escalatedBlockers = action.payload.escalatedBlockers
        state.replanTrigger = action.payload.replanTrigger
        state.stalled = action.payload.stalled
      })
      .addCase(fetchExecutionData.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to load execution data'
      })
  },
})

export const { clearExecution } = executionSlice.actions
export default executionSlice.reducer
